import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { stats } from '@/mocks/mogu-data';

const milestones = [
  { year: '2020', title: '平台创立', description: '蘑菇tv正式上线，专注高清直播领域，首批签约500位主播。' },
  { year: '2021', title: '快速发展', description: '签约主播突破1万人，日活用户达到500万，完成A轮融资。' },
  { year: '2022', title: '生态扩展', description: '上线短视频功能，内容覆盖游戏、娱乐、生活等多个领域，用户破2000万。' },
  { year: '2023', title: '技术突破', description: '自研流媒体技术上线，实现1080P超低延迟直播，开放主播扶持计划。' },
  { year: '2024', title: '行业领先', description: '签约主播突破5万，日活用户3000万，成为国内TOP3直播平台。' },
  { year: '2025', title: '全面升级', description: 'AI推荐系统全面上线，推出会员体系，国际版正式发布覆盖30+国家和地区。' },
  { year: '2026', title: '引领未来', description: '日活突破5000万，签约主播超10万，持续推动直播行业创新与发展。' },
];

const testimonials = [
  {
    name: '电竞小王子',
    role: '游戏主播 · 128万粉丝',
    content: '蘑菇tv的直播体验真的是我见过最好的，画质清晰延迟低，粉丝互动也很方便。平台还经常给主播流量扶持，让我能专注做好内容。',
    avatar: 'https://readdy.ai/api/search-image?query=Happy%20young%20Chinese%20male%20gamer%20portrait%20with%20genuine%20smile%20and%20casual%20hoodie%2C%20clean%20studio%20lighting&width=100&height=100&seq=testimonial-t1&orientation=squarish',
  },
  {
    name: '温暖小酒馆',
    role: '娱乐主播 · 96万粉丝',
    content: '从零开始做主播到现在，蘑菇tv给了我很多支持和帮助。平台的氛围特别好，观众也很友善，真的很感激这个大家庭。',
    avatar: 'https://readdy.ai/api/search-image?query=Grateful%20Chinese%20female%20streamer%20portrait%20with%20warm%20smile%20and%20soft%20lighting%2C%20clean%20studio%20background%2C%20genuine%20happy%20expression&width=100&height=100&seq=testimonial-t2&orientation=squarish',
  },
  {
    name: '厨房老李',
    role: '美食主播 · 62万粉丝',
    content: '做了一辈子厨师，没想到退休后还能在蘑菇tv上教大家做菜。观众们学得认真，我也教得开心，这就是我想要的生活。',
    avatar: 'https://readdy.ai/api/search-image?query=Wise%20older%20Chinese%20chef%20portrait%20with%20warm%20genuine%20smile%20and%20chef%20coat%2C%20clean%20studio%20lighting%2C%20experienced%20friendly%20look&width=100&height=100&seq=testimonial-t3&orientation=squarish',
  },
];

export default function AboutPage() {
  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '关于我们' },
            ]}
          />

          {/* Hero Banner */}
          <section className="relative rounded-2xl overflow-hidden mb-16 h-[300px] md:h-[400px] flex items-center">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20creative%20office%20space%20with%20colorful%20gradient%20lighting%20and%20streaming%20equipment%2C%20collaborative%20tech%20company%20atmosphere%2C%20warm%20coral%20and%20golden%20tones%2C%20clean%20futuristic%20aesthetic&width=1440&height=600&seq=about-hero-mogu&orientation=landscape"
              alt="蘑菇tv团队"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-black/20"></div>
            <div className="relative z-10 px-8 md:px-14">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-background-50">关于蘑菇tv</h1>
              <p className="mt-3 text-sm md:text-base text-background-200/90 max-w-lg leading-relaxed">
                专注高清直播与短视频内容，用技术创新和优质内容，连接每一个热爱生活的你。
              </p>
            </div>
          </section>

          {/* Stats */}
          <section className="mb-16">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center p-6 rounded-xl bg-background-100">
                  <div className="w-12 h-12 mx-auto rounded-xl bg-primary-100 flex items-center justify-center mb-3">
                    <i className={`${stat.icon} text-xl text-primary-600`}></i>
                  </div>
                  <div className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">{stat.value}</div>
                  <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Brand Story */}
          <section className="mb-16">
            <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-2">品牌故事</h2>
            <p className="text-sm text-foreground-500 mb-8">从零到一，蘑菇tv的成长之路</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  蘑菇tv诞生于2020年，创始团队来自国内顶级互联网公司。我们怀揣着"让每个人都能享受高质量直播内容"的使命，从零开始搭建了这个平台。
                </p>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  经过六年多的耕耘，蘑菇tv已经从一个小小的创业团队，成长为拥有超过<strong>10万签约主播</strong>、<strong>5000万日活用户</strong>的行业领先平台。我们覆盖了游戏、娱乐、生活、体育、美食、音乐、户外等数十个内容品类，为用户提供了丰富多元的视听体验。
                </p>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  蘑菇tv始终坚持技术驱动和内容为王的发展理念。我们投入大量资源自主研发流媒体传输技术，实现了1080P高清画质下的超低延迟直播。同时，AI智能推荐系统让每一位用户都能快速发现自己喜爱的内容和主播。
                </p>
              </div>
              <div className="rounded-xl overflow-hidden aspect-[4/3]">
                <img
                  src="https://readdy.ai/api/search-image?query=Creative%20team%20collaboration%20in%20modern%20office%20with%20large%20screens%20showing%20live%20streaming%20data%2C%20warm%20lighting%2C%20professional%20tech%20company%20atmosphere%2C%20diverse%20team&width=800&height=600&seq=about-story&orientation=landscape"
                  alt="蘑菇tv团队协作"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </section>

          {/* Milestones */}
          <section className="mb-16">
            <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-2">发展历程</h2>
            <p className="text-sm text-foreground-500 mb-8">六年深耕，砥砺前行</p>

            <div className="relative pl-8 border-l-2 border-primary-200 space-y-8">
              {milestones.map((milestone, index) => (
                <div key={milestone.year} className="relative">
                  <div className="absolute -left-[33px] w-4 h-4 rounded-full bg-primary-500 border-2 border-background-50"></div>
                  <div className="p-5 rounded-xl bg-background-100 hover:bg-background-200/70 transition-colors">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-bold">
                        {milestone.year}
                      </span>
                      <h3 className="text-sm font-semibold text-foreground-900">{milestone.title}</h3>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">{milestone.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Testimonials */}
          <section className="mb-16">
            <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-2">主播口碑</h2>
            <p className="text-sm text-foreground-500 mb-8">听听蘑菇tv主播们的心声</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((t) => (
                <div key={t.name} className="rounded-xl bg-background-100 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-11 h-11 rounded-full object-cover"
                      loading="lazy"
                    />
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">{t.name}</div>
                      <div className="text-xs text-foreground-500">{t.role}</div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed">{t.content}</p>
                  <div className="flex items-center gap-1 mt-3 text-accent-500">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section className="mb-16">
            <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-2">联系我们</h2>
            <p className="text-sm text-foreground-500 mb-8">有任何问题，欢迎与我们取得联系</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div className="rounded-xl bg-background-100 p-6 flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-xl text-primary-600"></i>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">商务合作</h3>
                  <p className="text-xs text-foreground-500">business@mogu.tv</p>
                </div>
              </div>
              <div className="rounded-xl bg-background-100 p-6 flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-secondary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-customer-service-2-line text-xl text-secondary-600"></i>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">客服支持</h3>
                  <p className="text-xs text-foreground-500">support@mogu.tv</p>
                  <p className="text-xs text-foreground-500 mt-0.5">7×24小时在线服务</p>
                </div>
              </div>
              <div className="rounded-xl bg-background-100 p-6 flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-accent-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-xl text-accent-600"></i>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">公司地址</h3>
                  <p className="text-xs text-foreground-500">北京市海淀区中关村科技园</p>
                  <p className="text-xs text-foreground-500 mt-0.5">蘑菇tv大厦 100080</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify([
            {
              '@context': 'https://schema.org',
              '@type': 'AboutPage',
              name: '关于蘑菇tv - 高清直播与视频平台',
              description: '蘑菇tv专注高清直播与短视频，连接每一个热爱生活的你。了解品牌故事、发展历程和联系方式。',
              url: 'https://mogu.tv/about',
              isPartOf: { '@type': 'WebSite', name: '蘑菇tv', url: 'https://mogu.tv/' },
            },
            {
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://mogu.tv/' },
                { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://mogu.tv/about' },
              ],
            },
          ]),
        }}
      />
    </>
  );
}